import { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { User, Building2, Users, Shield, Smartphone, Key, Save, Plus, Trash2, Mail } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TEAM_MEMBERS = [
  { name: "Jane Doe", email: "jane@acme.com", role: "Owner", status: "Active", lastLogin: "2 min ago" },
  { name: "John Smith", email: "john@acme.com", role: "Admin", status: "Active", lastLogin: "1 hour ago" },
  { name: "Alice Johnson", email: "alice@acme.com", role: "Developer", status: "Active", lastLogin: "3 days ago" },
  { name: "Bob Williams", email: "bob@acme.com", role: "Developer", status: "Invited", lastLogin: "Never" },
];

export default function Settings() {
  const { t } = useLanguage();
  const [mfaEnabled, setMfaEnabled] = useState(true);

  return (
    <div data-testid="settings-page">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight" data-testid="settings-title">Settings</h1>
        <p className="text-sm text-slate-500 mt-1">Manage your account, organization, and security preferences</p>
      </div>

      <Tabs defaultValue="profile">
        <TabsList className="bg-white border border-slate-200 h-10 p-0.5 mb-6 w-auto" data-testid="settings-tabs">
          <TabsTrigger value="profile" className="text-xs px-4 gap-2 data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-profile">
            <User className="w-3.5 h-3.5" /> Profile
          </TabsTrigger>
          <TabsTrigger value="organization" className="text-xs px-4 gap-2 data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-organization">
            <Building2 className="w-3.5 h-3.5" /> Organization
          </TabsTrigger>
          <TabsTrigger value="team" className="text-xs px-4 gap-2 data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-team">
            <Users className="w-3.5 h-3.5" /> Team
          </TabsTrigger>
          <TabsTrigger value="security" className="text-xs px-4 gap-2 data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-security">
            <Shield className="w-3.5 h-3.5" /> Security
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <div className="bg-white border border-slate-200 p-6 max-w-2xl" data-testid="profile-form">
            <h3 className="text-sm font-semibold text-slate-900 mb-6">Personal Information</h3>
            <div className="space-y-5">
              <div className="flex items-center gap-6 mb-6">
                <div className="w-16 h-16 bg-slate-100 flex items-center justify-center text-lg font-bold text-slate-600">JD</div>
                <div>
                  <button className="text-xs text-cyan-600 hover:text-cyan-700 font-medium uppercase tracking-wider" data-testid="change-avatar-btn">Change Avatar</button>
                  <p className="text-xs text-slate-400 mt-0.5">JPG, PNG. Max 2MB.</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">First Name</Label>
                  <Input defaultValue="Jane" className="mt-1.5 h-10 bg-white" data-testid="profile-first-name" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Last Name</Label>
                  <Input defaultValue="Doe" className="mt-1.5 h-10 bg-white" data-testid="profile-last-name" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Email</Label>
                <Input defaultValue="jane@acme.com" className="mt-1.5 h-10 bg-white" data-testid="profile-email" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Job Title</Label>
                <Input defaultValue="Senior Security Engineer" className="mt-1.5 h-10 bg-white" data-testid="profile-title" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Timezone</Label>
                <Select defaultValue="utc">
                  <SelectTrigger className="mt-1.5 h-10" data-testid="profile-timezone"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="utc">UTC (Coordinated Universal Time)</SelectItem>
                    <SelectItem value="ist">IST (India Standard Time)</SelectItem>
                    <SelectItem value="est">EST (Eastern Standard Time)</SelectItem>
                    <SelectItem value="pst">PST (Pacific Standard Time)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <button className="inline-flex items-center gap-2 bg-slate-900 text-white hover:bg-slate-800 transition-colors text-xs font-bold uppercase tracking-widest px-5 py-2.5" data-testid="save-profile-btn">
                <Save className="w-3.5 h-3.5" /> Save Changes
              </button>
            </div>
          </div>
        </TabsContent>

        {/* Organization Tab */}
        <TabsContent value="organization">
          <div className="bg-white border border-slate-200 p-6 max-w-2xl" data-testid="org-form">
            <h3 className="text-sm font-semibold text-slate-900 mb-6">Organization Details</h3>
            <div className="space-y-5">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Organization Name</Label>
                <Input defaultValue="Acme Corporation" className="mt-1.5 h-10 bg-white" data-testid="org-name" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Organization ID</Label>
                <div className="mt-1.5">
                  <code className="text-sm font-mono text-slate-600 bg-slate-50 px-3 py-2 border border-slate-200 block">org_acme_a1b2c3d4</code>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Industry</Label>
                <Select defaultValue="fintech">
                  <SelectTrigger className="mt-1.5 h-10" data-testid="org-industry"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fintech">Financial Services / FinTech</SelectItem>
                    <SelectItem value="defense">Defense & Government</SelectItem>
                    <SelectItem value="gaming">Gaming & iGaming</SelectItem>
                    <SelectItem value="web3">Web3 & Blockchain</SelectItem>
                    <SelectItem value="healthcare">Healthcare</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Webhook URL</Label>
                <Input defaultValue="https://acme.com/webhooks/qrng" className="mt-1.5 h-10 bg-white font-mono text-sm" data-testid="org-webhook" />
              </div>
              <button className="inline-flex items-center gap-2 bg-slate-900 text-white hover:bg-slate-800 transition-colors text-xs font-bold uppercase tracking-widest px-5 py-2.5" data-testid="save-org-btn">
                <Save className="w-3.5 h-3.5" /> Save Changes
              </button>
            </div>
          </div>
        </TabsContent>

        {/* Team Tab */}
        <TabsContent value="team">
          <div className="bg-white border border-slate-200" data-testid="team-section">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h3 className="text-sm font-semibold text-slate-900">Team Members</h3>
              <button className="inline-flex items-center gap-2 bg-slate-900 text-white hover:bg-slate-800 transition-colors text-xs font-bold uppercase tracking-widest px-4 py-2" data-testid="invite-member-btn">
                <Plus className="w-3.5 h-3.5" /> Invite
              </button>
            </div>
            <div>
              <div className="grid grid-cols-[1fr_100px_90px_120px_40px] bg-slate-50 border-b border-slate-200">
                {["Member","Role","Status","Last Login",""].map((h,i) => (
                  <div key={i} className="px-6 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</div>
                ))}
              </div>
              {TEAM_MEMBERS.map((m, i) => (
                <div key={i} className="grid grid-cols-[1fr_100px_90px_120px_40px] border-b border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid={`team-member-row-${i}`}>
                  <div className="px-6 py-3 flex items-center gap-3">
                    <div className="w-8 h-8 bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600">{m.name.split(' ').map(n => n[0]).join('')}</div>
                    <div>
                      <div className="text-sm font-medium text-slate-900">{m.name}</div>
                      <div className="text-xs text-slate-400">{m.email}</div>
                    </div>
                  </div>
                  <div className="px-6 py-3 flex items-center"><Badge variant="outline" className="text-[10px] font-medium">{m.role}</Badge></div>
                  <div className="px-6 py-3 flex items-center">
                    <Badge variant="outline" className={`text-[10px] ${m.status === 'Active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>{m.status}</Badge>
                  </div>
                  <div className="px-6 py-3 text-xs text-slate-500 flex items-center">{m.lastLogin}</div>
                  <div className="px-6 py-3 flex items-center">
                    {m.role !== 'Owner' && (
                      <button className="p-1 text-slate-400 hover:text-red-500" data-testid={`remove-member-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security">
          <div className="space-y-4 max-w-2xl">
            <div className="bg-white border border-slate-200 p-6" data-testid="mfa-settings">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-cyan-50 border border-cyan-200 flex items-center justify-center">
                    <Smartphone className="w-5 h-5 text-cyan-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">Multi-Factor Authentication</h3>
                    <p className="text-xs text-slate-500 mt-0.5">Add an extra layer of security using TOTP authenticator apps</p>
                    {mfaEnabled && (
                      <Badge className="mt-2 bg-emerald-50 text-emerald-700 text-[10px] border border-emerald-200">Enabled - Google Authenticator</Badge>
                    )}
                  </div>
                </div>
                <Switch checked={mfaEnabled} onCheckedChange={setMfaEnabled} data-testid="mfa-toggle" />
              </div>
            </div>
            <div className="bg-white border border-slate-200 p-6" data-testid="password-settings">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-10 h-10 bg-slate-100 flex items-center justify-center">
                  <Key className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-900">Change Password</h3>
                  <p className="text-xs text-slate-500 mt-0.5">Update your account password. Minimum 12 characters recommended.</p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Current Password</Label>
                  <Input type="password" className="mt-1.5 h-10 bg-white" data-testid="current-password" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">New Password</Label>
                  <Input type="password" className="mt-1.5 h-10 bg-white" data-testid="new-password" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Confirm New Password</Label>
                  <Input type="password" className="mt-1.5 h-10 bg-white" data-testid="confirm-password" />
                </div>
                <button className="inline-flex items-center gap-2 bg-slate-900 text-white hover:bg-slate-800 transition-colors text-xs font-bold uppercase tracking-widest px-5 py-2.5" data-testid="change-password-btn">
                  Update Password
                </button>
              </div>
            </div>
            <div className="bg-white border border-slate-200 p-6" data-testid="sessions-settings">
              <h3 className="text-sm font-semibold text-slate-900 mb-1">Active Sessions</h3>
              <p className="text-xs text-slate-500 mb-4">Manage your active login sessions across devices</p>
              <div className="space-y-3">
                {[
                  { device: "Chrome on macOS", ip: "192.168.1.100", location: "Mumbai, IN", current: true },
                  { device: "Firefox on Ubuntu", ip: "10.0.0.45", location: "Bangalore, IN", current: false },
                ].map((s, i) => (
                  <div key={i} className="flex items-center justify-between p-3 border border-slate-100" data-testid={`session-${i}`}>
                    <div>
                      <div className="text-sm text-slate-900">{s.device} {s.current && <Badge className="ml-2 bg-cyan-50 text-cyan-700 text-[9px] border-0">Current</Badge>}</div>
                      <div className="text-xs text-slate-400 mt-0.5">{s.ip} &middot; {s.location}</div>
                    </div>
                    {!s.current && (
                      <button className="text-xs text-red-500 hover:text-red-600 font-medium" data-testid={`revoke-session-${i}`}>Revoke</button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
